import React, { useMemo, useState } from 'react';
import { Stack, TextField, Typography } from '@mui/material';
import {
  PageHeader,
  SearchFilterBar,
  DataTable,
  StatusChip,
  flattenPages,
} from '../components';
import {
  useLotsPaginated,
  useExpiringLotsPaginated,
  useExpiredLotsPaginated,
} from '../hooks/useInventoryLots';
import { colors, formatCurrency, formatDate } from '../constants';

export default function InventoryLots() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [view, setView] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const allParams = useMemo(
    () => ({
      limit: 20,
      status: statusFilter || undefined,
      search: search || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [statusFilter, search, dateFrom, dateTo]
  );

  const allLots = useLotsPaginated(allParams);
  const expiringLots = useExpiringLotsPaginated({ limit: 20, days: 30 });
  const expiredLots = useExpiredLotsPaginated({ limit: 20 });

  const active = view === 'expiring' ? expiringLots : view === 'expired' ? expiredLots : allLots;
  const items = flattenPages(active);

  const columns = [
    {
      key: 'lotNumber',
      label: 'Lot',
      render: (row) => (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600, color: colors.navy }}>
            {row.lotNumber}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.gray500 }}>
            SKU: {row.sku}
          </Typography>
        </>
      ),
    },
    {
      key: 'expiryDate',
      label: 'Expiry',
      render: (row) =>
        row.expiryDate ? (
          <Typography
            variant="body2"
            sx={{ color: new Date(row.expiryDate) < new Date() ? colors.error : colors.gray600 }}
          >
            {formatDate(row.expiryDate)}
          </Typography>
        ) : (
          '-'
        ),
    },
    { key: 'currentQuantity', label: 'Qty', align: 'right' },
    { key: 'unitCost', label: 'Unit Cost', align: 'right', render: (row) => formatCurrency(row.unitCost) },
    { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
  ];

  return (
    <>
      <PageHeader title="Inventory Lots" subtitle="Lot-tracked stock and expiry" />

      <SearchFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Filter lots..."
        filters={[
          {
            label: 'View',
            value: view,
            allLabel: 'All Lots',
            onChange: (v) => setView(v || 'all'),
            options: [
              { value: 'expiring', label: 'Expiring' },
              { value: 'expired', label: 'Expired' },
            ],
          },
          {
            label: 'Status',
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
              { value: 'available', label: 'Available' },
              { value: 'reserved', label: 'Reserved' },
              { value: 'consumed', label: 'Consumed' },
              { value: 'expired', label: 'Expired' },
            ],
          },
        ]}
      >
        {view === 'all' && (
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              sx={{ backgroundColor: '#fff' }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              sx={{ backgroundColor: '#fff' }}
            />
          </Stack>
        )}
      </SearchFilterBar>

      <DataTable
        columns={columns}
        rows={items}
        loading={active.isLoading}
        emptyTitle="No lots found"
        hasNextPage={active.hasNextPage}
        isFetchingNextPage={active.isFetchingNextPage}
        fetchNextPage={active.fetchNextPage}
      />
    </>
  );
}
